/**
 * Design ⇄ Seat Assignment seam (ESAPA-104 / mapd-issues#659).
 *
 * A saved Design carries the room — Tables, Chairs, labels, colours and layout —
 * and nothing about who sits where (ADR-0001). That splits the work in two, and
 * both halves live here as pure functions over the Design's canvas JSON so they
 * can be covered without Fabric or the DOM:
 *
 *  - `stripSeatAssignments` takes seating *out* of a Design. It runs on the save
 *    path, which is why duplicating or reusing a Design gives you the room as a
 *    clean template rather than last time's attendees.
 *  - `applySeatAssignments` puts seating *back in* for display, from the Seat
 *    Assignment module's model. Views hydrate the room this way at load time,
 *    which is why there is no longer anything to reconcile: the canvas is
 *    rendered from the single owner instead of diffed against it.
 */

import { SeatAssignmentModule } from './SeatAssignmentModule';
import { assignmentsFromRows } from './seatRows';
import { SeatTable } from './types';

/** The grey outline a Chair with nobody in it is drawn with. */
const EMPTY_CHAIR_STROKE = '#808080';

/** An attendee as the seating views draw them onto a Chair. */
export interface SeatedAttendee {
  uniqueId: string;
  /** Alias of `uniqueId`, for views that key their lists on `id`. */
  id: string;
  email: string;
  name: string;
  initials: string;
  company: string;
  /** Shown by the seating views next to the attendee, when the event has one. */
  mealType?: string;
  checkedIn: boolean;
  originalChairType?: string;
  assignedTicket: {
    name: string;
    ticketId: string;
    uniqueId: string;
    checkedIn: boolean;
    tktprofileid?: string;
  };
}

/**
 * Seated attendees keyed by **Table Id**, never by Table Label: labels are
 * display-only, editable and not unique (ADR-0002), so joining on one merges
 * two Tables that happen to share a name.
 */
export type AttendeesByTable = Record<string, SeatedAttendee[]>;

const isObject = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object';

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const childObjects = (obj: Record<string, any>): any[] =>
  Array.isArray(obj.objects) ? obj.objects : [];

const isChair = (obj: any): boolean => isObject(obj) && obj.isChair === true;

const isTableGroup = (obj: any): boolean =>
  isObject(obj) && obj.isTable === true && isObject(obj.tableData);

/** The geometry a Chair keeps whether it is occupied or empty. */
const chairPlacement = (chair: Record<string, any>) => ({
  left: chair.left,
  top: chair.top,
  angle: chair.angle,
  scaleX: chair.scaleX,
  scaleY: chair.scaleY,
  originX: chair.originX ?? 'center',
  originY: chair.originY ?? 'center',
});

/** The shape a Chair is drawn as, whether it stands alone or wraps a name. */
const chairShapeOf = (chair: Record<string, any>): Record<string, any> => {
  if (chair.type === 'group') {
    const inner = childObjects(chair).find(
      (child: any) => child?.type === 'circle' || child?.type === 'rect'
    );
    if (inner) return inner;
  }
  return chair;
};

/**
 * The shape props a Chair is drawn with, defaulted. A Chair is drawn the same
 * way whether it is occupied or empty, so both directions read it from here.
 */
const chairGeometry = (shape: Record<string, any>): Record<string, any> =>
  shape.type === 'rect'
    ? {
        type: 'rect',
        width: shape.width ?? 30,
        height: shape.height ?? 30,
        rx: shape.rx ?? 5,
        ry: shape.ry ?? 5,
      }
    : { type: 'circle', radius: shape.radius ?? 15 };

// -----------------------------------------------------------------------------
// Taking seating out of a Design
// -----------------------------------------------------------------------------

/** An occupied Chair, rendered back down to the empty Chair it was drawn over. */
function emptyChairFrom(chair: Record<string, any>): Record<string, any> {
  const shape = chairShapeOf(chair);
  return {
    ...chairGeometry(shape),
    fill: 'transparent',
    stroke: EMPTY_CHAIR_STROKE,
    strokeWidth: shape.strokeWidth ?? 2,
    ...chairPlacement(chair),
    isChair: true,
    status: 'empty',
    selectable: false,
  };
}

function stripObject(obj: any): any {
  if (!isObject(obj)) return obj;

  if (isChair(obj)) return emptyChairFrom(obj);

  const next: Record<string, any> = { ...obj };
  delete next.attendeeData;

  if (isTableGroup(next)) {
    const tableData = { ...next.tableData };
    const chairCount = childObjects(next).filter(isChair).length;
    tableData.attendees = [];
    if ('capacity' in tableData) tableData.capacity = chairCount;
    if ('emptySeats' in tableData) tableData.emptySeats = chairCount;
    next.tableData = tableData;
  }

  if (Array.isArray(next.objects)) {
    next.objects = next.objects.map(stripObject);
  }
  return next;
}

/**
 * A copy of a Design's canvas JSON with every Seat Assignment removed: no
 * attendees on any Table, every Chair empty. Objects that are not part of a
 * seating plan are left exactly as they were, so this is a no-op on a badge or
 * floor-map Design.
 */
export function stripSeatAssignments<T>(templateData: T): T {
  if (!isObject(templateData)) return templateData;
  return stripObject(clone(templateData));
}

/** `stripSeatAssignments` across every page of a Design. */
export function stripPageDataSeatAssignments(pageData: any): any {
  if (!Array.isArray(pageData)) return pageData;
  return pageData.map((page) =>
    isObject(page) && page.templateData
      ? { ...page, templateData: stripSeatAssignments(page.templateData) }
      : page
  );
}

/**
 * A Design record — the envelope the panel and the canvas pass around, whose
 * `template` holds the canvas JSON — with every Seat Assignment taken out of the
 * room it carries.
 *
 * This is what makes a Design from one of the admin's other events usable here
 * (mapd-issues#662): the room comes across, the people do not, because the
 * Tickets they name belong to the event the Design came from. Table Ids survive
 * the copy deliberately, so a Table is the same Table in both events.
 */
export function stripDesignSeatAssignments<T extends Record<string, any>>(design: T): T {
  if (!isObject(design) || !isObject(design.template)) return design;

  const template = { ...design.template };
  if (template.data) {
    template.data = stripSeatAssignments(template.data);
  }
  if (Array.isArray(template.pageData)) {
    template.pageData = stripPageDataSeatAssignments(template.pageData);
  }
  return { ...design, template };
}

/** Whether a Design still carries anything about who sits where. */
export function designHasSeatAssignments(templateData: any): boolean {
  if (!isObject(templateData)) return false;
  let found = false;
  const walk = (obj: any) => {
    if (found || !isObject(obj)) return;
    if (obj.attendeeData) found = true;
    if (obj.status === 'occupied') found = true;
    if (isTableGroup(obj) && Array.isArray(obj.tableData.attendees) && obj.tableData.attendees.length) {
      found = true;
    }
    childObjects(obj).forEach(walk);
  };
  walk(templateData);
  return found;
}

// -----------------------------------------------------------------------------
// The room, as the Seat Assignment module wants it
// -----------------------------------------------------------------------------

/**
 * The Tables in a Design, in the shape the Seat Assignment module's model uses.
 * A Table without a Table Id is skipped: the module joins on the Table Id, never
 * on the editable Table Label (ADR-0002).
 */
export function tablesFromDesign(templateData: any): SeatTable[] {
  const tables: SeatTable[] = [];
  const walk = (obj: any) => {
    if (!isObject(obj)) return;
    if (isTableGroup(obj)) {
      const { tableId, tableNumber, tableName } = obj.tableData;
      const label = `${tableName ?? tableNumber ?? ''}`;
      if (tableId) {
        tables.push({
          tableId,
          label,
          chairCount: childObjects(obj).filter(isChair).length,
        });
      }
      return;
    }
    childObjects(obj).forEach(walk);
  };
  walk(templateData);
  return tables;
}

// -----------------------------------------------------------------------------
// Reading Seat Assignments out of the module
// -----------------------------------------------------------------------------

const initialsOf = (first: string, last: string): string =>
  `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase();

/**
 * Group attendees by the Table Id they are seated at, asking the **module**
 * where each Ticket sits rather than re-reading the raw `SeatNo`. That is what
 * makes the module the single source: a `SeatNo` naming no Table in this room is
 * dropped, and a Remote Change already applied to the module is reflected here
 * without another fetch.
 */
export function attendeesByTableFromModule(
  seatModule: Pick<SeatAssignmentModule, 'getAssignment' | 'getTable'>,
  rows: any[]
): AttendeesByTable {
  const byTable: AttendeesByTable = {};
  const seen = new Set<string>();

  (rows || []).forEach((row: any) => {
    const email = `${row?.email ?? ''}`.trim().toLowerCase();
    const name = `${row?.fname ?? ''} ${row?.lname ?? ''}`.trim();
    (Array.isArray(row?.tclist) ? row.tclist : []).forEach((ticket: any) => {
      const ticketId = ticket?.ticketid;
      if (!ticketId) return;

      const tableId = seatModule.getAssignment(ticketId);
      if (!tableId) return;
      if (!seatModule.getTable(tableId)) return;

      const uniqueId = `${email}-${ticketId}`;
      if (seen.has(uniqueId)) return;
      seen.add(uniqueId);

      const checkedIn = ticket.checkin === true || ticket.checkin === 'true';
      const attendee: SeatedAttendee = {
        uniqueId,
        id: uniqueId,
        email,
        name,
        initials: initialsOf(row?.fname, row?.lname),
        company: row?.company || '',
        mealType: row?.mealType || '',
        checkedIn,
        assignedTicket: {
          name: ticket.name || 'General Admission',
          ticketId,
          uniqueId,
          checkedIn,
          tktprofileid: ticket.tktprofileid,
        },
      };

      if (!byTable[tableId]) byTable[tableId] = [];
      byTable[tableId].push(attendee);
    });
  });

  return byTable;
}

// -----------------------------------------------------------------------------
// Putting seating back in, for display only
// -----------------------------------------------------------------------------

/**
 * Who sits at one Table, joined on the Table Id (ADR-0002). A Table with no
 * Table Id cannot be joined and so draws empty — the same Table the module
 * skipped when it read the room in `tablesFromDesign`.
 */
function attendeesForTable(tableData: Record<string, any>, byTable: AttendeesByTable): SeatedAttendee[] {
  const tableId = tableData.tableId;
  return tableId ? byTable[tableId] ?? [] : [];
}

/** An empty Chair, redrawn as the Chair an attendee is sitting in. */
function occupiedChairFrom(
  chair: Record<string, any>,
  attendee: SeatedAttendee,
  colors: Record<string, any> | undefined,
  strokeColor: string
): Record<string, any> {
  const geometry = chairGeometry(chairShapeOf(chair));
  // The group has to cover the shape it wraps: a circle's box is its diameter.
  const box =
    geometry.type === 'circle'
      ? { width: geometry.radius * 2, height: geometry.radius * 2 }
      : { width: geometry.width, height: geometry.height };

  return {
    type: 'group',
    ...box,
    ...chairPlacement(chair),
    objects: [
      {
        ...geometry,
        fill: colors?.chair || 'transparent',
        stroke: strokeColor,
        strokeWidth: 2,
        left: 0,
        top: 0,
        originX: 'center',
        originY: 'center',
        scaleX: 1,
        scaleY: 1,
      },
      {
        type: 'text',
        text: attendee.initials || 'NA',
        // Fabric hands `styles` straight back when it is not an array, so a text
        // object without this key enlivens with `styles` undefined and then
        // throws the moment the canvas is serialised — which the history stack
        // does immediately after load. The empty array is the serialised form of
        // "no per-character styling".
        styles: [],
        fontSize: 12,
        fill: colors?.text || '#000000',
        fontFamily: 'Times New Roman',
        textAlign: 'center',
        lineHeight: 1.2,
        left: 0,
        top: 0,
        originX: 'center',
        originY: 'center',
        scaleX: 1,
        scaleY: 1,
      },
    ],
    isChair: true,
    status: 'occupied',
    selectable: false,
    uniqueId: attendee.uniqueId,
    email: attendee.email,
    attendeeData: {
      ...attendee,
      seat: undefined,
      originalChairType: geometry.type,
    },
  };
}

export interface ApplySeatAssignmentsOptions {
  /** Chair outline by check-in state. Defaults to the seating views' palette. */
  checkedInStroke?: string;
  checkedOutStroke?: string;
}

const DEFAULT_CHECKED_IN_STROKE = '#38A169';
const DEFAULT_CHECKED_OUT_STROKE = '#808080';

/**
 * A copy of a Design's canvas JSON with the given Seat Assignments drawn onto
 * it. The Design itself is never modified, and attendees beyond a Table's Chair
 * count are left unseated rather than overflowing the room.
 */
export function applySeatAssignments(
  templateData: any,
  byTable: AttendeesByTable,
  options: ApplySeatAssignmentsOptions = {}
): any {
  if (!isObject(templateData)) return templateData;
  const checkedIn = options.checkedInStroke ?? DEFAULT_CHECKED_IN_STROKE;
  const checkedOut = options.checkedOutStroke ?? DEFAULT_CHECKED_OUT_STROKE;

  const hydrate = (obj: any): any => {
    if (!isObject(obj)) return obj;

    if (isTableGroup(obj)) {
      const chairs = childObjects(obj).filter(isChair);
      const seated = attendeesForTable(obj.tableData, byTable).slice(0, chairs.length);
      const colors = obj.tableData.colors;

      let index = 0;
      obj.objects = childObjects(obj).map((child: any) => {
        if (!isChair(child)) return hydrate(child);
        const attendee = seated[index];
        if (!attendee) return child;
        index += 1;
        return occupiedChairFrom(
          child,
          attendee,
          colors,
          attendee.assignedTicket.checkedIn ? checkedIn : checkedOut
        );
      });

      obj.tableData = {
        ...obj.tableData,
        attendees: seated,
        capacity: chairs.length,
        emptySeats: Math.max(0, chairs.length - seated.length),
      };
      return obj;
    }

    if (Array.isArray(obj.objects)) obj.objects = obj.objects.map(hydrate);
    return obj;
  };

  return hydrate(stripSeatAssignments(templateData));
}

/** `applySeatAssignments` across every page of a Design. */
export function applyPageDataSeatAssignments(
  pageData: any,
  byTable: AttendeesByTable,
  options?: ApplySeatAssignmentsOptions
): any {
  if (!Array.isArray(pageData)) return pageData;
  return pageData.map((page) =>
    isObject(page) && page.templateData
      ? { ...page, templateData: applySeatAssignments(page.templateData, byTable, options) }
      : page
  );
}

/**
 * The Seat Assignments for one Design, read through a throwaway module.
 *
 * Loading a Design is a render of the single owner, not a reconciliation
 * against it: the Design supplies the room, `fetchRows` supplies the people,
 * and the module decides which Ticket sits at which Table. Nothing is written
 * back, and nothing is compared with what the Design used to say.
 */
export async function seatingForDesign(
  pageData: any,
  fetchRows: () => Promise<any[]>
): Promise<AttendeesByTable> {
  const pages = Array.isArray(pageData) ? pageData : [];
  const tables = pages.flatMap((page: any) => tablesFromDesign(page?.templateData));
  if (!tables.length) return {};

  const rows = await fetchRows();
  const seatModule = new SeatAssignmentModule({
    tables,
    backend: {
      fetchAssignments: async () => assignmentsFromRows(rows),
      // Rendering a Design never writes a seat.
      updateSeats: async () => [],
    },
  });
  await seatModule.load();
  return attendeesByTableFromModule(seatModule, rows);
}
